import {
  Badge,
  Button,
  DocumentList,
  InfoPanel,
  TextField,
  Typography,
  Label,
  RadioGroup,
  RadioButton,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as UploadIcon } from "@king-ict/eupisi-ui/components/Icon/Upload.svg";
import { RequestStatusEnum } from "@king-ict/eupisi-ui/models/Request";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { RootState } from "../../../app/rootReducer";
import CriterionDataFetchStatusEnum from "../../../models/Criterion/CriterionDataFetchStatusEnum";
import CriterionProvingTypeEnum from "../../../models/Criterion/CriterionProvingTypeEnum";
import IntegrationEnum from "../../../models/Integration/IntegrationEnum";
import RequestCriterion from "../../../models/Request/RequestCriterion";
import { RequestDocumentData } from "../../../models/Request/RequestDocumentData";
import { getPersonsOibs } from "../../../slices/requestSlice";
import ChildrenOfParentsCriterionData from "../../RequestInfo/CriterionIntegrationData/ChildrenOfParentsCriterionData";
import PermanentAndTemporaryResidenceData from "../../RequestInfo/CriterionIntegrationData/PermanentAndTemporaryResidenceData";
import DocumentsModal from "../DocumentsModal/DocumentsModal";
import styles from "./CriterionItem.module.scss";
import { useCriterionDocuments } from "./useCriterionDocuments";

interface Props {
  consentAccepted: boolean;
  selected: boolean;
  criterion: RequestCriterion;
  criterionDocuments: RequestDocumentData[];
  onSelect: (criterion: RequestCriterion, isSelected: boolean) => void;
  onDocumentsChange: (
    criterion: RequestCriterion,
    documents: RequestDocumentData[]
  ) => void;
  onRemoveDocumentFromList: (
    criterion: RequestCriterion,
    document: RequestDocumentData
  ) => void;
  setDocumentsModalOpenId: Function;
  documentsModalOpenId?: number;
  onCloseDocumentModal: Function;
  allDocuments: RequestDocumentData[];
  criterionError: boolean;
}

export default function CriterionItem({
  consentAccepted,
  selected,
  criterion,
  criterionDocuments,
  onSelect,
  onDocumentsChange,
  setDocumentsModalOpenId,
  documentsModalOpenId,
  onCloseDocumentModal,
  onRemoveDocumentFromList,
  allDocuments,
  criterionError,
}: Props) {
  const requestDataState = useSelector(
    (state: RootState) => state.request.request
  );

  const { t } = useTranslation();
  const personsOibs = useSelector(getPersonsOibs);
  const handleSelectChange = (checked: boolean) => {
    criterion = { ...criterion, hasBeenMarkedByUser: true };
    onSelect(criterion, checked);
    if (!checked && criterionDocuments && criterionDocuments.length > 0) {
      handleRemoveDocuments(criterionDocuments);
    }
  };

  const {
    handleUploadDocuments,
    handleUploadExistingDocument,
    handleConfirmDocumentsModal,
    handleCloseDocumentsModal,
    handleOpenDocumentsModal,
    handleDownloadDocument,
    handleRemoveDocumentFromList,
    handleRemoveDocument,
    handleRemoveDocuments,
  } = useCriterionDocuments({
    criterion: criterion,
    onRemoveDocumentFromList: onRemoveDocumentFromList,
    onDocumentsChange: onDocumentsChange,
    setDocumentsModalOpenId: setDocumentsModalOpenId,
    onCloseDocumentModal: onCloseDocumentModal,
  });

  const isIntegrationInfoVisible =
    consentAccepted &&
    criterion.provingTypeId &&
    (criterion.provingTypeId & CriterionProvingTypeEnum.Integration) ===
      CriterionProvingTypeEnum.Integration;

  const getStatusColor = (status: CriterionDataFetchStatusEnum) => {
    switch (status) {
      case CriterionDataFetchStatusEnum.Success:
        return "success";
      case CriterionDataFetchStatusEnum.Fail:
        return "danger";
      case CriterionDataFetchStatusEnum.Unknown:
        return undefined;
      default:
        return undefined;
    }
  };

  const getIntegrationData = (data: string, integrationId?: number) => {
    switch (integrationId) {
      case IntegrationEnum.MUP1:
      case IntegrationEnum.MUP2:
      case IntegrationEnum.MUP3:
        return <PermanentAndTemporaryResidenceData data={data} />;

      case IntegrationEnum.ParentChild:
        return <ChildrenOfParentsCriterionData data={data} />;

      default:
        return (
          <Row>
            <Col md={12}>
              <Typography>{data}</Typography>
            </Col>
          </Row>
        );
    }
  };

  return (
    <Row>
      <Col xs={12}>
        <RadioGroup
          name={criterion.criterionName}
          title={criterion.criterionName}
          required={false}
          onChange={(e) => handleSelectChange(e.target.value === "1")}
          errorText={
            criterion.hasBeenMarkedByUser === false
              ? "Molimo označavanje kriterija koje zadovoljavate za potrebe bodovanja pri upisu u dječji vrtić."
              : undefined
          }
        >
          <br />
          <div style={{ display: "inline-flex" }}>
            <RadioButton
              key={"Da"}
              label={"Da"}
              value={1}
              checked={criterion.hasBeenMarkedByUser && selected === true}
              ariaLabel={t("accessibility.radioAnswer")}
            />
            <div style={{ marginLeft: "1em" }}></div>
            <RadioButton
              key={"Ne"}
              label={"Ne"}
              value={0}
              checked={criterion.hasBeenMarkedByUser && selected === false}
              ariaLabel={t("accessibility.radioAnswer")}
            />
          </div>
        </RadioGroup>
      </Col>
      <Col
        xs={12}
        className={`${styles.gutter} ${
          selected ? styles.pointsSpacing : styles.bottomSpacing
        }`}
      >
        {criterion.isDirectEnrollment ? (
          <Typography component={"span"}>
            {t("criteriaStep.directEnrollment")}
          </Typography>
        ) : (
          <>
            <Badge
              className={styles.pointsBadge}
              value={criterion.points || 0}
              variant="small"
              color="success"
            />
            <Typography component={"span"}>
              {t("criteriaStep.points")}
            </Typography>
          </>
        )}
      </Col>
      {selected && (
        <Col xs={12}>
          <Row className={`${styles.gutter}`}>
            {isIntegrationInfoVisible && (
              <Col md={6}>
                <Row>
                  <Col md={12}>
                    <Typography component="h3" variant="smallHeading">
                      {t("criteriaStep.integrationHeader", {
                        integrationName: criterion.integrationName,
                      })}
                    </Typography>
                    <InfoPanel
                      message={t("criteriaStep.integrationInfoMessage", {
                        documentDescription:
                          criterion.provingDocumentDescription,
                      })}
                      color="success"
                    />
                  </Col>
                </Row>
                {criterion.fetchedIntegrationData &&
                criterion.integrationData ? (
                  <>
                    <Row className={styles.integrationStatusRow}>
                      <Col xs={6}>
                        <TextField
                          plaintext
                          bold
                          label={t("criteriaStep.applicantParentIntegration")}
                          value={personsOibs.applicantParentOib}
                        />
                      </Col>
                      <Col xs={6} className={styles.status}>
                        <Label
                          color={getStatusColor(
                            criterion.integrationData.applicantParentData
                              .fetchStatus
                          )}
                        >
                          {criterion.integrationData.applicantParentData
                            .fetchStatus ===
                          CriterionDataFetchStatusEnum.Success
                            ? t("criteriaStep.successIntegration")
                            : t("criteriaStep.failIntegration")}
                        </Label>
                      </Col>
                    </Row>
                    {getIntegrationData(
                      criterion.integrationData.applicantParentData.data,
                      criterion.integrationId
                    )}
                    {personsOibs.otherParentOib != null &&
                      criterion.integrationData.otherParentData && (
                        <>
                          <Row
                            className={`${styles.integrationStatusRow} ${styles.integrationOtherParentTitle}`}
                          >
                            <Col xs={6}>
                              <TextField
                                plaintext
                                bold
                                label={t("criteriaStep.otherParentIntegration")}
                                value={personsOibs.otherParentOib}
                              />
                            </Col>
                            <Col xs={6} className={styles.status}>
                              <Label
                                color={getStatusColor(
                                  criterion.integrationData.otherParentData
                                    .fetchStatus
                                )}
                              >
                                {criterion.integrationData.otherParentData
                                  .fetchStatus ===
                                CriterionDataFetchStatusEnum.Success
                                  ? t("criteriaStep.successIntegration")
                                  : t("criteriaStep.failIntegration")}
                              </Label>
                            </Col>
                          </Row>
                          {criterion.integrationId ===
                            IntegrationEnum.ParentChild &&
                            getIntegrationData(
                              criterion.integrationData.otherParentData.data,
                              criterion.integrationId
                            )}
                        </>
                      )}
                    {personsOibs.childOib != null &&
                      criterion.integrationData.childData && (
                        <>
                          <Row
                            className={`${styles.integrationStatusRow} ${styles.integrationOtherParentTitle}`}
                          >
                            <Col xs={6}>
                              <TextField
                                plaintext
                                bold
                                label={t("criteriaStep.childIntegration")}
                                value={personsOibs.childOib}
                              />
                            </Col>
                            <Col xs={6} className={styles.status}>
                              <Label
                                color={getStatusColor(
                                  criterion.integrationData.childData
                                    .fetchStatus
                                )}
                              >
                                {criterion.integrationData.childData
                                  .fetchStatus ===
                                CriterionDataFetchStatusEnum.Success
                                  ? t("criteriaStep.successIntegration")
                                  : t("criteriaStep.failIntegration")}
                              </Label>
                            </Col>
                          </Row>
                          {getIntegrationData(
                            criterion.integrationData.childData.data,
                            criterion.integrationId
                          )}
                        </>
                      )}
                  </>
                ) : (
                  <></>
                )}
              </Col>
            )}
            <Col
              className={`${styles.rowSpace} ${
                isIntegrationInfoVisible ? styles.borderLeftInt : ""
              }`}
              md={isIntegrationInfoVisible ? 6 : 12}
            >
              <Row className={styles.rowSpace}>
                <Col md={12} className={styles.documents}>
                  <Typography component="h3" variant="smallHeading">
                    {t("criteriaStep.documentHeader")}
                  </Typography>
                  <InfoPanel
                    message={t("criteriaStep.documentInfoMessage", {
                      documentDescription: criterion.provingDocumentDescription,
                    })}
                    color="success"
                  />
                </Col>
                <Col md={12}>
                  <Button
                    color="primary"
                    disabled={criterionDocuments.length >= 10}
                    outline
                    icon={<UploadIcon />}
                    onClick={handleOpenDocumentsModal}
                  >
                    {t("documentUpload.additionalDocumentationAttach")}
                  </Button>
                  <DocumentsModal
                    open={
                      documentsModalOpenId === criterion.institutionCriterionId
                    }
                    allDocuments={allDocuments}
                    onClose={handleCloseDocumentsModal}
                    onConfirm={handleConfirmDocumentsModal}
                    onUpload={handleUploadDocuments}
                    onRemove={handleRemoveDocument}
                    onDownload={handleDownloadDocument}
                    onExistingDocumentClick={handleUploadExistingDocument}
                  />
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={12}>
                  <Typography variant="smallHeading">
                    {t("documentUpload.additionalDocumentationList")}
                  </Typography>
                </Col>
                {criterionDocuments && criterionDocuments.length > 0 ? (
                  <Col md={12}>
                    <DocumentList
                      documents={criterionDocuments}
                      onRemoveClick={handleRemoveDocumentFromList}
                      onDocumentClick={handleDownloadDocument}
                      fullWidth
                    />
                  </Col>
                ) : (
                  <Col md={12}>
                    <Typography variant="base" color="light">
                      {t("criteriaStep.noDocuments")}
                    </Typography>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </Col>
      )}
      {criterionError && (
        <Col xs={12}>
          <InfoPanel
            message={t("criteriaStep.documentIsRequred")}
            color="danger"
          />
        </Col>
      )}

      <Col xs={12} className={styles.bottomSpacing}>
        <hr />
      </Col>
    </Row>
  );
}
